import { useMemo } from "react";
import ResultTable from "../../components/ResultTable";
import DraggableAnalysisRobot from "../../components/DraggableAnalysisRobot";
import { GameScreenEmbed } from "../../components/GameScreenEmbed";
import NhRobotHackLoading from "../NH/components/NhRobotHackLoading";
import BcrResultPopup from "./BcrResultPopup";
import {
  BCR_FORMULAS,
  analyzeAllBaccaratFormulas,
  recentRoadPreview,
  type BcrFormulaId,
} from "../../utils/analyzeBaccaratFormula";

type Phase = "idle" | "scanning" | "result";

type Props = {
  tableTitle: string;
  loading: boolean;
  tableData: any[];
  playerCount: number;
  tieCount: number;
  bankerCount: number;
  formula: BcrFormulaId;
  phase: Phase;
  gameScreenUrl: string;
  onBack: () => void;
  onFormulaChange: (formula: BcrFormulaId) => void;
  onReveal: () => void;
};

export default function BcrFigmaView({
  tableTitle,
  loading,
  tableData,
  playerCount,
  tieCount,
  bankerCount,
  formula,
  phase,
  gameScreenUrl,
  onBack,
  onFormulaChange,
  onReveal,
}: Props) {
  const analyses = useMemo(
    () => analyzeAllBaccaratFormulas(tableData),
    [tableData]
  );
  const analysis =
    analyses.find((item) => item.formula === formula) ?? analyses[0];
  const recent = useMemo(() => recentRoadPreview(tableData, 10), [tableData]);

  return (
    <div className="bcr-flow">
      <div className="bcr-flow__grid">
        <main className="bcr-flow__board">
          <div className="bcr-flow__head">
            <button type="button" onClick={onBack}>
              ◂ BÀN
            </button>
            <strong>{tableTitle}</strong>
            <span>SUPER VIP - WIN {analysis.forecast}%</span>
          </div>

          <div className="bcr-flow__road">
            {loading ? (
              <div className="bcr-flow__loading">ĐANG TẢI CẦU...</div>
            ) : (
              <ResultTable tableData={tableData} />
            )}
          </div>

          <div className="bcr-flow__stats">
            <span className="is-banker">Nhà cái {bankerCount}</span>
            <span className="is-player">Người chơi {playerCount}</span>
            <span className="is-tie">Hòa {tieCount}</span>
          </div>

          <div className="bcr-flow__recent" aria-label="Cầu gần nhất">
            <span>Cầu gần nhất</span>
            <ol>
              {recent.length === 0 ? (
                <li className="is-empty">—</li>
              ) : (
                recent.map((bead, index) => (
                  <li
                    key={`${bead}-${index}`}
                    className={
                      bead === "P" ? "is-player" : bead === "B" ? "is-banker" : "is-tie"
                    }
                  >
                    {bead}
                  </li>
                ))
              )}
            </ol>
          </div>

          <div className="bcr-flow__formula">
            <p>Công thức dự đoán — đọc cầu gần nhất, chọn 1 hướng</p>
            {BCR_FORMULAS.map((item) => {
              const snap = analyses.find((row) => row.formula === item.id) ?? analysis;
              return (
                <button
                  type="button"
                  key={item.id}
                  className={formula === item.id ? "is-active" : ""}
                  onClick={() => onFormulaChange(item.id)}
                >
                  <strong>{item.label}</strong>
                  <em>
                    {snap.winner} · {snap.buttonNote}
                  </em>
                </button>
              );
            })}
          </div>

          <p className="bcr-flow__hint">{analysis.hint}</p>
          <button
            type="button"
            className="bcr-flow__reveal"
            disabled={phase === "scanning"}
            onClick={onReveal}
          >
            {phase === "scanning" ? "ĐANG QUÉT DỮ LIỆU..." : "XEM KẾT QUẢ (-5 ĐIỂM)"}
          </button>
        </main>

        <aside className="bcr-flow__external">
          <GameScreenEmbed
            url={gameScreenUrl}
            name="GG88"
            title={`Baccarat ${tableTitle}`}
          />
        </aside>
      </div>

      <DraggableAnalysisRobot
        message={analysis.hint}
        imageSrc="/assets/robot.gif"
        className="bcr-flow__robot-widget"
        hideBubble={phase !== "idle"}
        topSlot={
          phase === "scanning" ? (
            <NhRobotHackLoading progress={78} />
          ) : phase === "result" ? (
            <BcrResultPopup tableTitle={tableTitle} analysis={analysis} />
          ) : null
        }
      />
    </div>
  );
}
